/**
 * src/App.tsx
 * ------------
 * Root application component for the Helix Customer Chat frontend.
 *
 * Fully implemented this will:
 *   - Apply the org appearance theme (CSS variable injection) on mount
 *     by reading VITE_ORG_ID and calling the appearance API
 *   - Provide React Router routes:
 *       /         → ChatScreen (main chat interface)
 *       /embed    → ChatScreen in iframe-friendly mode
 *       /voice    → VoiceToVoiceScreen (full-screen voice mode)
 *   - Render global Toast notification container
 *   - Persist auth state via useAuth hook (checks existing JWT on load)
 *   - Handle online/offline events with a status banner
 *
 * Key dependencies:
 *   react-router-dom, src/views/ChatScreen.tsx,
 *   src/components/voice/VoiceToVoiceScreen.tsx,
 *   src/hooks/useAppearance.ts, src/hooks/useAuth.ts,
 *   src/components/ui/Toast.tsx
 */
import { useEffect } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { ChatScreen } from '@/views/ChatScreen'
import { VoiceToVoiceScreen } from '@/components/voice/VoiceToVoiceScreen'
import { Toast } from '@/components/ui/Toast'
import { useAppearance } from '@/hooks/useAppearance'
import { useAuth } from '@/hooks/useAuth'
import { getCurrentOrgId } from '@/lib/org'

function AppShell() {
  const { loadAppearance } = useAppearance()
  const { restoreSession } = useAuth()
  const orgId = getCurrentOrgId()

  useEffect(() => {
    loadAppearance(orgId)
    // Admin branding changes go live without a refresh (server cache is 30s).
    const poll = setInterval(() => loadAppearance(orgId), 45_000)
    restoreSession()
    return () => clearInterval(poll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  return (
    <>
      <Routes>
        <Route path="/" element={<ChatScreen />} />
        <Route path="/embed" element={<ChatScreen embedded />} />
        <Route path="/voice" element={<VoiceToVoiceScreen />} />
      </Routes>
      <Toast />
    </>
  )
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppShell />
    </BrowserRouter>
  )
}
